import { CloseOutlined, BookOutlined, CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useDarkMode, useThemeColors, useAppSelector, useAppDispatch } from "redux/hooks";
import { useSession } from "components/wrappers/SessionProvider";
import { UpdateSettingsAction } from "redux/actions/UpdateSettings";
import { ConditionalTooltip } from "./ConditionalTooltip";
import { Input, notification } from "antd";

export function SavedQueriesView(props: {
    isOpen: boolean;
    onClose: () => void;
    onOpenAddQuery?: (query?: string, queryName?: string) => void;
    isAddQueryOpen?: boolean;
}) {
    const cardRef = useRef<HTMLDivElement>(null);
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const dispatch = useAppDispatch();
    const session = useSession();
    const existingQueries = useAppSelector(state => state.user.settings?.queries || {});
    const [editingName, setEditingName] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");

    const handleCopy = (queryText: string) => {
        navigator.clipboard.writeText(queryText);
        notification.success({
            message: 'Query copied',
            description: 'Query has been copied to clipboard.',
            placement: 'bottomLeft',
            duration: 2
        });
    };

    const handleDelete = async (name: string) => {
        try {
            const updatedQueries = { ...existingQueries };
            delete updatedQueries[name];

            await dispatch(UpdateSettingsAction(session, { queries: updatedQueries }));

            notification.success({
                message: 'Query deleted',
                description: `Query "${name}" has been deleted.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to delete query',
                description: error instanceof Error ? error.message : 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handleStartRename = (name: string) => {
        setEditingName(name);
        setEditingValue(name);
    };

    const handleCancelRename = () => {
        setEditingName(null);
        setEditingValue("");
    };

    const handleSaveRename = async (oldName: string) => {
        const newName = editingValue.trim();
        if (!newName) {
            notification.error({
                message: 'Query name required',
                description: 'Please enter a name for this query.',
                placement: 'bottomLeft',
                duration: 3
            });
            return;
        }

        if (oldName === newName) {
            setEditingName(null);
            setEditingValue("");
            return;
        }

        if (existingQueries[newName]) {
            notification.error({
                message: 'Query name already exists',
                description: 'Please choose a different name.',
                placement: 'bottomLeft',
                duration: 3
            });
            return;
        }

        try {
            const updatedQueries = { ...existingQueries };
            const queryText = updatedQueries[oldName];
            delete updatedQueries[oldName];
            updatedQueries[newName] = queryText;

            await dispatch(UpdateSettingsAction(session, { queries: updatedQueries }));

            notification.success({
                message: 'Query renamed',
                description: `Query has been renamed to "${newName}".`,
                placement: 'bottomLeft',
                duration: 2
            });

            setEditingName(null);
            setEditingValue("");
        } catch (error) {
            notification.error({
                message: 'Failed to rename query',
                description: error instanceof Error ? error.message : 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    // Handle clicking outside the card
    const { isOpen, onClose } = props;
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            // Don't close if add query menu is open
            if (props.isAddQueryOpen) {
                return;
            }

            const target = event.target as HTMLElement;

            // Don't close if clicking on the Ant Design modal or its overlay
            if (target.closest('.ant-modal') ||
                target.closest('.ant-modal-mask') ||
                target.closest('.ant-modal-wrap') ||
                target.closest('.ant-modal-content')) {
                return;
            }

            // Don't close if clicking on the QueriesView (add query) modal
            // Check if the click is inside an element that contains the queries-textarea class
            const queriesViewCard = target.closest('.queries-textarea')?.closest('div[style*="90vw"]');
            if (queriesViewCard) {
                return;
            }

            if (cardRef.current && !cardRef.current.contains(target)) {
                onClose();
            }
        };

        // Add event listener with a small delay to prevent immediate closing
        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose, props.isAddQueryOpen]);

    if (!props.isOpen) {
        return null;
    }

    // Filter and sort queries based on search term
    const queryEntries = Object.entries(existingQueries as Record<string, string>)
        .filter(([name]) => {
            if (!searchTerm.trim()) return true;
            return name.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .sort(([a], [b]) => a.localeCompare(b));

    return (
        <>
            <style>{`
                .saved-queries-search-input input::placeholder,
                .saved-queries-search-input .ant-input::placeholder {
                    color: ${themeColors.textColorMuted} !important;
                }
                .saved-queries-search-input .ant-input-clear-icon {
                    color: ${themeColors.textColorMuted} !important;
                }
                .saved-queries-search-input .ant-input-clear-icon:hover {
                    color: ${themeColors.textColor} !important;
                }
            `}</style>
            {/* Overlay */}
            <div
                onClick={props.isAddQueryOpen ? undefined : props.onClose}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 2000,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {/* Saved Queries Card */}
                <div
                    ref={cardRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: themeColors.backgroundColor,
                        borderRadius: "8px",
                        boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.15)",
                        width: "90vw",
                        height: "90vh",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header with close button */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 20px",
                            borderBottom: `1px solid ${themeColors.borderColor}`,
                        }}
                    >
                        <h2 style={{ margin: 0, fontSize: "1.5em", fontWeight: "600", color: themeColors.textColor }}>
                            Saved Queries
                        </h2>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            {props.onOpenAddQuery && (
                                <ConditionalTooltip title="Add new query">
                                    <button
                                        onClick={() => {
                                            props.onOpenAddQuery?.();
                                        }}
                                        style={{
                                            background: themeColors.primaryColor,
                                            border: `1px solid ${themeColors.primaryColor}`,
                                            borderRadius: "4px",
                                            padding: "6px 12px",
                                            cursor: "pointer",
                                            color: "#ffffff",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            transition: "all 0.2s"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = themeColors.primaryColorHover;
                                            e.currentTarget.style.borderColor = themeColors.primaryColorHover;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = themeColors.primaryColor;
                                            e.currentTarget.style.borderColor = themeColors.primaryColor;
                                        }}
                                    >
                                        <PlusOutlined />
                                        Add Query
                                    </button>
                                </ConditionalTooltip>
                            )}
                            <ConditionalTooltip title={props.isAddQueryOpen ? "Close add query menu first" : "Close saved queries"}>
                                <button
                                    onClick={props.isAddQueryOpen ? undefined : props.onClose}
                                    disabled={props.isAddQueryOpen}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: props.isAddQueryOpen ? "not-allowed" : "pointer",
                                        padding: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: props.isAddQueryOpen ? themeColors.textColorMuted : themeColors.iconColor,
                                        fontSize: "18px",
                                        opacity: props.isAddQueryOpen ? 0.5 : 1,
                                    }}
                                >
                                    <CloseOutlined />
                                </button>
                            </ConditionalTooltip>
                        </div>
                    </div>

                    {/* Content area */}
                    <div
                        style={{
                            padding: "20px",
                            flex: 1,
                            overflow: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                        }}
                    >
                        {/* Search input */}
                        <div>
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search queries by name..."
                                prefix={<SearchOutlined style={{ color: themeColors.textColorMuted }} />}
                                allowClear
                                className="saved-queries-search-input"
                                style={{
                                    backgroundColor: darkMode ? themeColors.backgroundColorSecondary : themeColors.backgroundColor,
                                    color: themeColors.textColor,
                                    borderColor: themeColors.borderColorSecondary
                                }}
                            />
                        </div>
                        {queryEntries.length === 0 ? (
                            <div style={{
                                textAlign: "center",
                                padding: "40px 20px",
                                color: themeColors.textColorMuted
                            }}>
                                {searchTerm.trim() ? "No queries match your search" : "No saved queries"}
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                {queryEntries.map(([name, queryText]) => (
                                    <div
                                        key={name}
                                        style={{
                                            border: `1px solid ${themeColors.borderColor}`,
                                            borderRadius: "8px",
                                            padding: "16px",
                                            backgroundColor: themeColors.backgroundColorSecondary,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "8px"
                                        }}
                                    >
                                        <div style={{ width: "100%" }}>
                                            {editingName === name ? (
                                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                                    <Input
                                                        value={editingValue}
                                                        onChange={(e) => setEditingValue(e.target.value)}
                                                        placeholder="Enter query name..."
                                                        onPressEnter={() => handleSaveRename(name)}
                                                        style={{
                                                            backgroundColor: darkMode ? themeColors.backgroundColorSecondary : themeColors.backgroundColor,
                                                            color: themeColors.textColor,
                                                            borderColor: themeColors.borderColorSecondary,
                                                            fontWeight: "600"
                                                        }}
                                                    />
                                                    <div style={{
                                                        fontFamily: "monospace",
                                                        fontSize: "0.85em",
                                                        color: themeColors.textColorMuted,
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        maxHeight: "100px",
                                                        overflow: "auto",
                                                        padding: "8px",
                                                        backgroundColor: darkMode ? "#1f1f1f" : "#f7f7f7",
                                                        borderRadius: "4px"
                                                    }}>
                                                        {String(queryText)}
                                                    </div>
                                                    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                                                        <button
                                                            onClick={handleCancelRename}
                                                            style={{
                                                                background: "transparent",
                                                                border: `1px solid ${themeColors.borderColorSecondary}`,
                                                                borderRadius: "4px",
                                                                padding: "4px 12px",
                                                                cursor: "pointer",
                                                                color: themeColors.textColor,
                                                                fontSize: "13px"
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={() => handleSaveRename(name)}
                                                            style={{
                                                                background: themeColors.primaryColor,
                                                                border: `1px solid ${themeColors.primaryColor}`,
                                                                borderRadius: "4px",
                                                                padding: "4px 12px",
                                                                cursor: "pointer",
                                                                color: "#ffffff",
                                                                fontSize: "13px"
                                                            }}
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        marginBottom: "8px"
                                                    }}>
                                                        <div style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px"
                                                        }}>
                                                            <BookOutlined style={{ color: themeColors.iconColor, fontSize: "16px" }} />
                                                            <h4 style={{
                                                                margin: 0,
                                                                fontSize: "1em",
                                                                fontWeight: "600",
                                                                color: themeColors.textColor
                                                            }}>
                                                                {name}
                                                            </h4>
                                                        </div>
                                                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                                            <ConditionalTooltip title="Copy query">
                                                                <button
                                                                    onClick={() => handleCopy(String(queryText))}
                                                                    style={{
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        cursor: "pointer",
                                                                        color: themeColors.iconColor,
                                                                        padding: "4px 8px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        borderRadius: "4px",
                                                                        transition: "background-color 0.2s, color 0.2s"
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.backgroundColor = themeColors.hoverBackground;
                                                                        e.currentTarget.style.color = themeColors.textColor;
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.backgroundColor = "transparent";
                                                                        e.currentTarget.style.color = themeColors.iconColor;
                                                                    }}
                                                                >
                                                                    <CopyOutlined />
                                                                </button>
                                                            </ConditionalTooltip>
                                                            <ConditionalTooltip title="Edit query">
                                                                <button
                                                                    onClick={() => {
                                                                        props.onOpenAddQuery?.(String(queryText), name);
                                                                    }}
                                                                    style={{
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        cursor: "pointer",
                                                                        color: themeColors.iconColor,
                                                                        padding: "4px 8px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        borderRadius: "4px",
                                                                        transition: "background-color 0.2s, color 0.2s"
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.backgroundColor = themeColors.hoverBackground;
                                                                        e.currentTarget.style.color = themeColors.textColor;
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.backgroundColor = "transparent";
                                                                        e.currentTarget.style.color = themeColors.iconColor;
                                                                    }}
                                                                >
                                                                    <EditOutlined />
                                                                </button>
                                                            </ConditionalTooltip>
                                                            <ConditionalTooltip title="Delete query">
                                                                <button
                                                                    onClick={() => handleDelete(name)}
                                                                    style={{
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        cursor: "pointer",
                                                                        color: "#ff4d4f",
                                                                        padding: "4px 8px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        borderRadius: "4px",
                                                                        transition: "background-color 0.2s"
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.backgroundColor = "rgba(255, 77, 79, 0.1)";
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.backgroundColor = "transparent";
                                                                    }}
                                                                >
                                                                    <DeleteOutlined />
                                                                </button>
                                                            </ConditionalTooltip>
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        fontFamily: "monospace",
                                                        fontSize: "0.85em",
                                                        color: themeColors.textColorMuted,
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        maxHeight: "100px",
                                                        overflow: "auto",
                                                        padding: "8px",
                                                        backgroundColor: darkMode ? "#1f1f1f" : "#f7f7f7",
                                                        borderRadius: "4px",
                                                        width: "100%"
                                                    }}>
                                                        {String(queryText)}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

